import { useCallback, useEffect, useMemo, useState } from "react";
import { useCachedTestNameMap, useCachedFxRange } from "../hooks/useCachedData";
import { useSystemScores } from "../hooks/useSystemScores";
import { useScreen } from "../context/ScreenProvider";
import { useFilters } from '../context/FilterProvider';
import bloodGroupings from "../../lambda/health-recommendations/blood_groupings.json";
import { calculateAllSystemScores } from "../util/SystemScoringUtil";
import { getCachedProfile, setCachedProfile } from "../util/CacheUtil";
import { getUserProfile } from "../components/API";

import { useAuth } from '../hooks/useAuth';
import { useBloodTestFiles } from '../hooks/useBloodTestFiles';
import { useEffectiveSelectedFile } from '../hooks/useEffectiveSelectedFile';
import { useFilteredTests } from '../hooks/useFilteredTests';
import { useTestEditing } from '../hooks/useTestEditing';
import { useRecommendations } from '../hooks/useRecommendations';
import { useProtocol } from '../hooks/useProtocol';
import useAssessment from '../hooks/useAssessment';
import UploadProgressToast from '../components/UploadProgressToast/UploadProgressToast';
import { FeatureGateProvider } from '../context/FeatureGateProvider';
import { tierHasFeature } from '../util/featureConfig';

import MobileHome from './MobileHome';
import DesktopHome from './DesktopHome';

function Home({ doctorViewMode = false, readOnly = false, patientId = null, patientName = null, tabPortalRef = null, exportPortalRef = null }) {
    const {
        selectedSystem, selectedFile, tags,
        highlightedRecommendations,
        selectSystem, forceSelectSystem,
        selectFile, setTags,
        setHighlightedSystems,
        clearFilters, clearAll,
    } = useFilters();

    const { isMobile } = useScreen();
    const { user } = useAuth({ doctorViewMode: doctorViewMode || readOnly });
    const { testNameMap, normalizeTestName } = useCachedTestNameMap();
    const { getFxRange } = useCachedFxRange();

    const {
        bloodTestFiles, loadingFiles, uploadingState, setUploadingState,
        uploadProgresses, dismissUploadProgress,
        refreshUserFiles, handleFileInput, userFiles, setUserFiles, mergeDraw,
    } = useBloodTestFiles({
        user, patientId, normalizeTestName, doctorViewMode, readOnly,
        // normalizeTestName is callable before its map arrives and silently
        // returns raw lab names until then.
        namesReady: !!testNameMap,
    });

    const { effectiveSelectedFile, selectedFileIndex } = useEffectiveSelectedFile({
        bloodTestFiles, selectedFile, selectFile,
    });

    const { filteredTests, allTests, testsForExport } = useFilteredTests({
        bloodTestFiles, effectiveSelectedFile, selectedSystem, normalizeTestName,
    });

    const {
        editTests, newTest, setNewTest,
        addEditTest, updateEditTest, createNewTest,
    } = useTestEditing({ user, bloodTestFiles, effectiveSelectedFile, userFiles });

    // Extract sex for system score calculation
    const isViewingOther = doctorViewMode || readOnly;
    const [profileData, setProfileData] = useState(() => isViewingOther ? null : getCachedProfile());

    useEffect(() => {
        if (!profileData && user) {
            getUserProfile(isViewingOther ? patientId : null)
                .then(data => {
                    if (!isViewingOther) setCachedProfile(data);
                    setProfileData(data);
                })
                .catch(() => {});
        }
    }, [user, profileData, isViewingOther, patientId]);

    const sex = useMemo(() => {
        return user?.attributes?.sex || profileData?.sex || 'male';
    }, [user, profileData]);

    const {
        savedProtocol, setSavedProtocol,
        protocolLocked, setProtocolLocked,
        hasActiveDoctor, effectiveLocked,
        lockedByName, lockLoading,
        saveProtocol, toggleLock, canRegenerate,
    } = useProtocol({
        userFiles, setUserFiles, patientId, doctorViewMode, user, bloodTestFiles,
        // The fingerprint resolves marker aliases through the same map the grouping
        // and the merge use, so it stays put across a merge that changed no result.
        normalizeTestName, namesReady: !!testNameMap,
    });

    const {
        recommendations, setRecommendations,
        recommendationMode, setRecommendationMode,
        loadingRecommendations, selectedAlternatives,
        handleSelectAlternative, handleGetRecommendations, handleGetAllDefaults,
    } = useRecommendations({ filteredTests, getFxRange, sex, savedProtocol, protocolLocked, effectiveLocked, saveProtocol, doctorViewMode });

    // --- Assessment summary for focus dashboard ---
    const { summary: assessmentSummary, prevSummary: prevAssessmentSummary, loadSummary } = useAssessment();
    useEffect(() => { loadSummary(isViewingOther ? patientId : null); }, [loadSummary, isViewingOther, patientId]);

    // --- Local UI state ---
    const [activeTab, setActiveTab] = useState('dashboard');
    const [selectedTest, setSelectedTest] = useState(null);

    // When patientId changes (doctor selects a different patient), reload files
    useEffect(() => {
        if (patientId && user) {
            setUserFiles(null);
            clearAll();
            setRecommendations(null);
            setSelectedTest(null);
            setProfileData(null);
            refreshUserFiles();
        }
    }, [patientId, user]);

    // Clear selectedTest when switching away from "All Tests" tab
    useEffect(() => {
        if (effectiveSelectedFile !== "all") {
            setSelectedTest(null);
            setHighlightedSystems([]);
        }
    }, [effectiveSelectedFile]);

    // --- System scoring ---

    const testsForScoring = useMemo(() => {
        if (!bloodTestFiles) return [];
        if (effectiveSelectedFile === "all" || effectiveSelectedFile === "new") return allTests;
        if (typeof effectiveSelectedFile === 'number' && bloodTestFiles[effectiveSelectedFile]) {
            return bloodTestFiles[effectiveSelectedFile].data.body || [];
        }
        return allTests;
    }, [bloodTestFiles, effectiveSelectedFile, allTests]);

    const { systemScoresArray, isLoading: isLoadingScores } = useSystemScores(testsForScoring, sex);

    const allFileScores = useMemo(() => {
        if (!bloodTestFiles || bloodTestFiles.length === 0) return [];
        const sorted = [...bloodTestFiles].sort((a, b) => {
            const dateA = a.data?.header?.testdate || '';
            const dateB = b.data?.header?.testdate || '';
            return String(dateB).localeCompare(String(dateA));
        });
        const getFxRangeForMarker = (markerName) => getFxRange(markerName, sex);
        return sorted.map((file) => {
            const tests = file.data?.body || [];
            const scores = calculateAllSystemScores(tests, bloodGroupings, getFxRangeForMarker);
            return { file, date: file.data?.header?.testdate, scores };
        });
    }, [bloodTestFiles, getFxRange, sex]);

    const previousScoresMap = useMemo(() => {
        if (!allFileScores.length) return {};
        let currentSortedIndex;
        if (effectiveSelectedFile === "all" || effectiveSelectedFile === "new") {
            currentSortedIndex = 0;
        } else if (typeof effectiveSelectedFile === 'number') {
            const sf = bloodTestFiles[effectiveSelectedFile];
            currentSortedIndex = allFileScores.findIndex(fs => fs.file === sf);
        } else {
            return {};
        }
        const currentFileScores = allFileScores[currentSortedIndex]?.scores || null;
        const result = {};
        if (currentFileScores) {
            Object.keys(currentFileScores).forEach(systemId => {
                for (let i = currentSortedIndex + 1; i < allFileScores.length; i++) {
                    const olderScore = allFileScores[i].scores?.[systemId]?.score;
                    if (olderScore != null) {
                        result[systemId] = olderScore;
                        break;
                    }
                }
            });
        }
        return result;
    }, [allFileScores, effectiveSelectedFile, bloodTestFiles]);

    // --- Test selection handlers ---

    const handleClearSelectedTest = useCallback(() => {
        setSelectedTest(null);
        setHighlightedSystems([]);
    }, []);

    const handleTestClick = useCallback((test, systemKey) => {
        const isDeselecting = selectedTest?.test_name === test.test_name
            && selectedTest?._groupKey === systemKey;
        if (isDeselecting) {
            setSelectedTest(null);
            setHighlightedSystems([]);
        } else {
            setSelectedTest({ ...test, _groupKey: systemKey });
            const testName = test.alias || test.test_name;
            const relatedSystems = Object.entries(bloodGroupings)
                .filter(([_, config]) => Object.keys(config.markers).includes(testName))
                .map(([systemId]) => systemId);
            setHighlightedSystems(relatedSystems);
        }
    }, [selectedTest]);

    // --- Feature gating ---
    // The Elis tab is visible to everyone (except read-only share views). Non-premium
    // patients see a locked/paywalled panel instead of the live chat. Doctors viewing a
    // patient always have access (their own entitlement governs).
    const showElisTab = !readOnly;
    const elisLocked = readOnly ? false
        : doctorViewMode ? false
        : !tierHasFeature(user?.tier ?? 0, 'elis_chat');
    const elisTrialUsed = profileData?.trialUsed === true;

    // --- Shared props for layout components ---
    const sharedProps = {
        user, doctorViewMode, readOnly, patientId, patientName, showElisTab, elisLocked, elisTrialUsed, tabPortalRef, exportPortalRef,
        bloodTestFiles, loadingFiles, uploadingState, setUploadingState,
        refreshUserFiles, handleFileInput, userFiles, setUserFiles, mergeDraw,
        effectiveSelectedFile, selectedFileIndex,
        filteredTests, allTests, testsForExport,
        editTests, newTest, setNewTest,
        addEditTest, updateEditTest, createNewTest,
        normalizeTestName, getFxRange, sex,
        recommendations, setRecommendations,
        recommendationMode, setRecommendationMode,
        loadingRecommendations, selectedAlternatives,
        handleSelectAlternative, handleGetRecommendations, handleGetAllDefaults,
        highlightedRecommendations,
        savedProtocol, protocolLocked, hasActiveDoctor, effectiveLocked, lockedByName, lockLoading, saveProtocol, toggleLock, canRegenerate,
        systemScoresArray, isLoadingScores, previousScoresMap,
        profileData,
        selectedTest, handleClearSelectedTest, handleTestClick,
        selectedSystem, selectedFile, tags,
        selectSystem, forceSelectSystem, selectFile, setTags,
        setHighlightedSystems, clearFilters, clearAll,
        activeTab, setActiveTab,
        assessmentSummary,
        prevAssessmentSummary,
        userFocuses: profileData?.focuses || [],
    };

    const layout = isMobile ? <MobileHome {...sharedProps} /> : <DesktopHome {...sharedProps} />;

    return (
        <FeatureGateProvider tier={user?.tier ?? 0}>
            {layout}
            <UploadProgressToast
                uploadProgresses={uploadProgresses}
                onDismiss={dismissUploadProgress}
            />
        </FeatureGateProvider>
    );
}

export default Home;
